"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React, { useState, useEffect } from "react";
import { Search, Menu as MenuIcon, ChevronDown } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerTrigger, DrawerClose } from "@/components/ui/drawer";
import { NavigationMenu, NavigationMenuContent, NavigationMenuItem, NavigationMenuLink, NavigationMenuList, NavigationMenuTrigger, navigationMenuTriggerStyle } from "@/components/ui/navigation-menu";
import { MenuProvider, useMenu, type MenuItem } from "@/integrations/wordpress/WordPressMenuProvider";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
function DesktopSubmenuItems({
  items
}: {
  items: MenuItem[];
}) {
  return <ul className="grid min-w-[200px] gap-1 p-2 bg-popover text-popover-foreground rounded-lg border border-border shadow-md">
      {items.map((item, i) => <li key={item.id || `sub-${i}`} data-index={i} className="relative group/sub">
          {item.children && item.children.length > 0 ? <div className="flex flex-col">
              <Link to={item.href || "#"} className="flex items-center justify-between px-3 py-2 text-sm font-medium rounded-md text-foreground hover:bg-muted transition-colors">
                <span>{item.label}</span>
                <ChevronDown className="h-3.5 w-3.5 -rotate-90 text-muted-foreground" />
              </Link>
              <div className="pl-3 mt-1 border-l border-border">
                <DesktopSubmenuItems items={item.children} />
              </div>
            </div> : <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="flex items-center px-3 py-2 text-sm font-medium rounded-md text-foreground hover:bg-muted transition-colors">
                {item.label}
              </Link>
            </NavigationMenuLink>}
        </li>)}
    </ul>;
}
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex items-center gap-2">
        <div className="h-9 w-20 bg-muted animate-pulse rounded-md" />
        <div className="h-9 w-24 bg-muted animate-pulse rounded-md" />
        <div className="h-9 w-20 bg-muted animate-pulse rounded-md" />
        <div className="h-9 w-28 bg-muted animate-pulse rounded-md" />
      </div>;
  }
  if (!menuItems || menuItems.length === 0) {
    return <nav className="flex items-center gap-1">
        <Link to="/PetHealthPage" className="px-3.5 py-2 text-sm font-medium text-foreground hover:text-foreground/80 rounded-md transition-colors">
          Pet Health
        </Link>
        <Link to="/NutritionPage" className="px-3.5 py-2 text-sm font-medium text-foreground hover:text-foreground/80 rounded-md transition-colors">
          Nutrition
        </Link>
        <Link to="/BehaviorTrainingPage" className="px-3.5 py-2 text-sm font-medium text-foreground hover:text-foreground/80 rounded-md transition-colors">
          Behavior & Training
        </Link>
        <Link to="/BreedsPage" className="px-3.5 py-2 text-sm font-medium text-foreground hover:text-foreground/80 rounded-md transition-colors">
          Breeds
        </Link>
        <Link to="/ProductsReviewsPage" className="px-3.5 py-2 text-sm font-medium text-foreground hover:text-foreground/80 rounded-md transition-colors">
          Reviews
        </Link>
      </nav>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-1">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id || `nav-${i}`} data-index={i}>
            {item.children && item.children.length > 0 ? <>
                <NavigationMenuTrigger className="bg-transparent text-foreground hover:bg-muted focus:bg-muted font-medium text-sm px-3.5 py-2">
                  {item.label}
                </NavigationMenuTrigger>
                <NavigationMenuContent>
                  <DesktopSubmenuItems items={item.children} />
                </NavigationMenuContent>
              </> : <NavigationMenuLink asChild>
                <Link to={item.href || "#"} className="inline-flex h-9 items-center justify-center rounded-md px-3.5 py-2 text-sm font-medium text-foreground hover:bg-muted transition-colors">
                  {item.label}
                </Link>
              </NavigationMenuLink>}
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileSubmenuItems({
  items,
  onItemClick
}: {
  items: MenuItem[];
  onItemClick: () => void;
}) {
  return <ul className="flex flex-col gap-1 pl-3 border-l border-border mt-1">
      {items.map((item, i) => <li key={item.id || `mob-sub-${i}`} data-index={i}>
          <Link to={item.href || "#"} onClick={onItemClick} className="block py-2 px-2 text-sm font-medium text-foreground hover:bg-muted rounded-md transition-colors">
            {item.label}
          </Link>
          {item.children && item.children.length > 0 && <MobileSubmenuItems items={item.children} onItemClick={onItemClick} />}
        </li>)}
    </ul>;
}
function MobileMenuConsumer({
  onClose
}: {
  onClose: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-3 py-4">
        <div className="h-8 w-full bg-muted animate-pulse rounded-md" />
        <div className="h-8 w-full bg-muted animate-pulse rounded-md" />
        <div className="h-8 w-full bg-muted animate-pulse rounded-md" />
        <div className="h-8 w-full bg-muted animate-pulse rounded-md" />
      </div>;
  }
  if (!menuItems || menuItems.length === 0) {
    const fallbackLinks = [{
      label: "Home",
      to: "/HomePage"
    }, {
      label: "Dogs",
      to: "/DogsPage"
    }, {
      label: "Cats",
      to: "/CatsPage"
    }, {
      label: "Pet Health",
      to: "/PetHealthPage"
    }, {
      label: "Nutrition",
      to: "/NutritionPage"
    }, {
      label: "Behavior & Training",
      to: "/BehaviorTrainingPage"
    }, {
      label: "Breeds",
      to: "/BreedsPage"
    }, {
      label: "Products & Reviews",
      to: "/ProductsReviewsPage"
    }, {
      label: "About PetsWhirl",
      to: "/AboutPetsWhirlPage"
    }];
    return <nav className="flex flex-col gap-1 py-2">
        {fallbackLinks.map((item, i) => <div key={item.to} data-index={i}>
            <Link to={item.to} onClick={onClose} className="block px-3 py-2.5 text-base font-medium text-foreground hover:bg-muted rounded-md transition-colors">
              {item.label}
            </Link>
          </div>)}
      </nav>;
  }
  return <nav className="flex flex-col gap-1 py-2">
      {menuItems.map((item, i) => <div key={item.id || `mob-${i}`} data-index={i} className="py-0.5">
          <Link to={item.href || "#"} onClick={onClose} className="block px-3 py-2.5 text-base font-medium text-foreground hover:bg-muted rounded-md transition-colors">
            {item.label}
          </Link>
          {item.children && item.children.length > 0 && <MobileSubmenuItems items={item.children} onItemClick={onClose} />}
        </div>)}
    </nav>;
}
export default function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section id="header" data-nav="light" className="sticky top-0 z-40 w-full bg-background/95 backdrop-blur-sm border-b border-border transition-colors">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex h-18 items-center justify-between gap-4">
          <div className="flex items-center gap-6">
            <Link to="/" className="flex items-center focus-visible:outline-ring rounded-md">
              <WvcLogo className="h-7 w-auto" />
            </Link>
          </div>

          <div className="hidden lg:flex lg:items-center lg:gap-1">
            <MenuProvider menu_id="16" className="header-menu-desktop">
              <DesktopMenuConsumer />
            </MenuProvider>
          </div>

          <div className="flex items-center gap-2">
            <Button asChild variant="ghost" size="icon" className="text-foreground hover:bg-muted focus-visible:ring-ring" aria-label="Search articles and guides">
              <Link to="/SearchResultsPage">
                <Search className="h-5 w-5" />
              </Link>
            </Button>

            <div className="lg:hidden">
              <SafeHydrate><Drawer open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
                <DrawerTrigger asChild>
                  <Button variant="ghost" size="icon" className="text-foreground hover:bg-muted focus-visible:ring-ring" aria-label="Open menu">
                    <MenuIcon className="h-5 w-5" />
                  </Button>
                </DrawerTrigger>
                <DrawerContent className="bg-background max-h-[85vh] p-6 border-t border-border">
                  <DrawerHeader className="px-0 pt-0 pb-4 border-b border-border flex items-center justify-between text-left">
                    <DrawerTitle className="text-lg font-semibold text-foreground">
                      Navigation
                    </DrawerTitle>
                    <DrawerClose asChild>
                      <Button variant="outline" size="sm">
                        Close
                      </Button>
                    </DrawerClose>
                  </DrawerHeader>

                  <div className="overflow-y-auto py-4">
                    <MenuProvider menu_id="16" className="header-menu-mobile">
                      <MobileMenuConsumer onClose={() => setMobileMenuOpen(false)} />
                    </MenuProvider>

                    <div className="mt-6 pt-4 border-t border-border">
                      <Button asChild className="w-full justify-center">
                        <Link to="/SearchResultsPage" onClick={() => setMobileMenuOpen(false)}>
                          <Search className="mr-2 h-4 w-4" />
                          Search Pet Guides
                        </Link>
                      </Button>
                    </div>
                  </div>
                </DrawerContent>
              </Drawer></SafeHydrate>
            </div>
          </div>
        </div>
      </div>
    </section>;
}